import { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import useTranslator from "../../../../hooks/useTranslator";
import { Badge, Button, Card, Icon, Skeleton } from "../../../../components/design";
import { MOBILE_SESSION_STATE } from "../mobileSessionState";
import styles from "../authenticateYourId.module.css";

interface ContinueOnPhoneScreenProps {
  /** empty while the phone session is being created: the QR is a skeleton */
  qrUrl: string;
  copyUrl: string;
  sessionState: string;
  onCancel: () => void;
}

const isTerminalState = (state: string): boolean =>
  [
    MOBILE_SESSION_STATE.COMPLETED,
    MOBILE_SESSION_STATE.FAILED,
    MOBILE_SESSION_STATE.EXPIRED,
    MOBILE_SESSION_STATE.CANCELLED,
  ].includes(state as any);

const statusLabels: Record<string, string> = {
  [MOBILE_SESSION_STATE.PENDING]: "connectPhoneStatusPending",
  [MOBILE_SESSION_STATE.PHONE_OPENED]: "connectPhoneStatusOpened",
  [MOBILE_SESSION_STATE.FRONT_CAPTURED]: "connectPhoneStatusFront",
  [MOBILE_SESSION_STATE.BACK_CAPTURED]: "connectPhoneStatusBack",
  [MOBILE_SESSION_STATE.SUBMITTED]: "connectPhoneStatusSubmitted",
  [MOBILE_SESSION_STATE.COMPLETED]: "connectPhoneStatusCompleted",
  [MOBILE_SESSION_STATE.FAILED]: "connectPhoneStatusFailed",
  [MOBILE_SESSION_STATE.EXPIRED]: "connectPhoneStatusExpired",
  [MOBILE_SESSION_STATE.CANCELLED]: "connectPhoneStatusCancelled",
};

const getTranslatedText = (
  _t: (key: string, context?: string) => string,
  key: string,
  fallback: string
) => {
  return _t(key);
};

const ContinueOnPhoneScreen = ({
  qrUrl,
  copyUrl,
  sessionState,
  onCancel,
}: ContinueOnPhoneScreenProps) => {
  const { _t } = useTranslator();
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (!isCopied) return;
    const timeout = window.setTimeout(() => setIsCopied(false), 2000);
    return () => window.clearTimeout(timeout);
  }, [isCopied]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(copyUrl);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = copyUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
    setIsCopied(true);
  };

  const isCreating = !qrUrl;
  const isTerminal = isTerminalState(sessionState);
  const isInProgress =
    !isTerminal && sessionState !== MOBILE_SESSION_STATE.PENDING;

  const statusKey = statusLabels[sessionState] || "connectPhoneStatusPending";
  const statusLabel = _t(statusKey);

  const title = isTerminal
    ? getTranslatedText(_t, "connectPhoneTitleDone", "You're all set")
    : getTranslatedText(_t, "continueOnYourPhone", "Continue on your mobile");

  const description = isTerminal
    ? statusLabel
    : getTranslatedText(
        _t,
        "continueOnYourPhoneDescription",
        "Scan the QR code with your phone camera. When you finish the photos, we'll continue here automatically."
      );

  return (
    <div
      className={`${styles.customKycUploadScreen} ${styles.continueOnPhoneScreen}`}
    >
      <div className={styles.continueOnPhoneCopy}>
        <h3>{title}</h3>
        <p>{description}</p>
        {isInProgress && (
          <Badge
            tone="neutral"
            className={styles.continueOnPhoneStatusBadge}
            role="status"
            aria-live="polite"
          >
            <span
              className={styles.continueOnPhoneSpinner}
              aria-hidden="true"
            />
            <span>{statusLabel}</span>
          </Badge>
        )}
      </div>

      <Card as="div" className={styles.continueOnPhoneMedia}>
        {isTerminal ? (
          <div
            className={`${styles.continueOnPhoneTerminalIcon} ${
              sessionState === MOBILE_SESSION_STATE.COMPLETED
                ? styles.success
                : styles.error
            }`}
          >
            <Icon
              name={
                sessionState === MOBILE_SESSION_STATE.COMPLETED
                  ? "check"
                  : "close"
              }
              size={32}
            />
          </div>
        ) : (
          <div className={styles.continueOnPhoneQrWrapper}>
            {isCreating ? (
              <Skeleton width={168} height={168} radius="sm" />
            ) : (
              <QRCode
                value={qrUrl}
                size={168}
                level="M"
                bgColor="#ffffff"
                fgColor="#253837"
              />
            )}
          </div>
        )}
      </Card>

      <div className={styles.customKycUploadActionPanel}>
        {!isTerminal && (
          <>
            <Button
              size="lg"
              block
              iconStart={<Icon name="link" />}
              loading={isCreating}
              onClick={handleCopyLink}
            >
              {isCopied
                ? getTranslatedText(_t, "copied", "Copied")
                : getTranslatedText(_t, "copyLink", "Copy link")}
            </Button>
            <Button
              variant="secondary"
              size="lg"
              block
              disabled={isCreating}
              onClick={onCancel}
            >
              {getTranslatedText(
                _t,
                "continueOnComputer",
                "Continue on computer"
              )}
            </Button>
          </>
        )}
        {isTerminal && sessionState !== MOBILE_SESSION_STATE.COMPLETED && (
          <Button size="lg" block onClick={onCancel}>
            {getTranslatedText(_t, "connectPhoneGoBack", "Go back")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ContinueOnPhoneScreen;
